import { Avatar, Box, ListItemButton, Typography } from "@mui/material";
import * as React from "react";
import { ISessionState } from "../../../../models/session/sessionState";
import { IFolderTreeNode } from "source/models/document/folderTreeNode";
import { ListStateIconExpanded } from "source/components/framework/List/ListStateIconExpanded";
import FolderTwoToneIcon from "@mui/icons-material/FolderTwoTone";
import { useState } from "react";
import { FileType, Permission } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { PopupMenu } from "source/components/framework/PopupMenu";
import { EditToolButton } from "source/components/framework/Button/EditToolButton";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { CopyToolButton } from "source/components/framework/Button/CopyToolButton";
import { AppIconFolder } from "source/components/framework/Icon/AppIconFolder";
import { AttachmentIcon } from "source/components/attachments/AttachmentIcon";

export interface IDocumentFolderTreeNodeProps {
    sessionState: ISessionState;
    companyId: string;
    editMode: boolean;
    node: IFolderTreeNode;
    selectedId?: string;
    isFirstNode: boolean;
    onClick: (node: IFolderTreeNode) => void;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onCopy?: () => void;
  }
  
export const DocumentTreeFolderNode = (props: IDocumentFolderTreeNodeProps) => {
    const [hovered, setHovered] = useState(false);

    const handleNodeClick = (e) => {
        e.stopPropagation();
        props.onClick(props.node);
    };

    const isRoot = !props.node.parent;
    const hasChildren = props.node.children.length > 0;
    const selected = props.selectedId === props.node.id;

    const canEditOwnCompanyFolder = props.sessionState &&
        (
            props.sessionState.isSuperAdmin ||
            props.sessionState.companyId === props.companyId && props.node.permission > Permission.Read && (!isRoot || props.sessionState.isCompanyAdmin)
        );
    const canEditSharedFolder = props.sessionState &&
        (
            props.sessionState.isSuperAdmin ||
            props.sessionState.companyId !== props.companyId && (props.node.permission > Permission.Read || props.sessionState.isCompanyAdmin && props.node.permission > Permission.None)
        );
    const canEditFolder = canEditOwnCompanyFolder || canEditSharedFolder;
    const canDeleteFolder = props.sessionState &&
        (
            props.sessionState.isSuperAdmin ||
            props.node.parent && props.node.parent.permission > Permission.Read
        );
    const popupMenuHasItems = !isRoot && props.onCopy ||
        canEditFolder && props.onEdit ||
        canDeleteFolder && !isRoot && props.onDelete;

    if (!props.node || (props.node.parent && props.node.getAncesterIsCollapsed())) return null;

    return (
        <ListItemButton
            selected={selected}
            onClick={handleNodeClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                sx={{ paddingLeft: (8 * props.node.level).toString(10) + "px", width: "100%" }}
            >
                {!props.isFirstNode && (
                    <ListStateIconExpanded
                        collapsed={!props.node.expanded}
                        hasChildren={hasChildren}
                        noMargin
                    />
                )}
                {(!props.node.image
                    ? (props.node.fileType === FileType.Unknown
                        ? (props.node.permission > Permission.Read
                            ? <AppIconFolder sx={{ "width": 24, "height": 24 }} />
                            : <FolderTwoToneIcon sx={{ "width": 24, "height": 24 }} />
                        )
                        : <AttachmentIcon fileType={props.node.fileType} color="#E9F0FA" strokeColor="#848688" />
                    )
                    : <Avatar
                        src={props.node.image}
                        sx={{ "width": 24, "height": 24, "backgroundColor": "#bfc9ca" }}
                    >
                    </Avatar>
                )}
                <Typography
                    sx={{
                        fontWeight: selected ? 600 : 400,
                        marginLeft: "10px",
                        maxWidth: "calc(100% - " + (20 + 24 + 8) + "px)",
                        maxHeight: "40px",
                        overflow: "hidden"
                    }}
                    title={props.node.name}
                >
                    {props.node.name}
                </Typography>
            </Box>
            {selected && hovered && popupMenuHasItems &&
                <PopupMenu>
                    {props.editMode && canEditFolder && !!props.onEdit &&
                        <EditToolButton tooltip={Translations.FolderProperties} onClick={(e) => {
                            e.stopPropagation();
                            props.onEdit(props.node.id);
                        }}/>
                    }
                    {!isRoot && !!props.onCopy &&
                        <CopyToolButton tooltip={Translations.CopyOrMoveFolder} onClick={(e) => {
                            e.stopPropagation();
                            props.onCopy();
                        }} />
                    }
                    {props.editMode && canDeleteFolder && !isRoot && !!props.onDelete &&
                        <RemoveToolButton tooltip={Translations.RemoveFolder} onClick={(e) => {
                            e.stopPropagation();
                            props.onDelete(props.node.id);
                        }} />
                    }
                </PopupMenu>
            }
        </ListItemButton>
    );
};
